import type { ReactNode } from 'react';

interface LuxuryTableProps {
  children: ReactNode;
  className?: string;
}

/**
 * Luxury table wrapper with dark theme styling.
 *
 * Features:
 * - Dark graphite background (bg-graphite)
 * - Gold table headers (text-gold uppercase text-xs)
 * - Slate-dark borders (border-slate-dark)
 * - Hover row effect (hover:bg-gold/5)
 * - JetBrains Mono for numbers (use .num class or font-mono)
 *
 * Usage:
 * <LuxuryTable>
 *   <thead>
 *     <tr>
 *       <th>Header</th>
 *     </tr>
 *   </thead>
 *   <tbody>
 *     <tr>
 *       <td>Data</td>
 *       <td className="num">$1,234</td>
 *     </tr>
 *   </tbody>
 * </LuxuryTable>
 */
export function LuxuryTable({
  children,
  className = '',
}: LuxuryTableProps) {
  return (
    <div className={`overflow-x-auto ${className}`}>
      <table
        className={`
          w-full border-collapse text-sm text-ivory

          [&_th]:text-left
          [&_th]:text-sm
          [&_th]:uppercase
          [&_th]:tracking-[0.12em]
          [&_th]:text-gold
          [&_th]:font-semibold
          [&_th]:px-4
          [&_th]:py-3
          [&_th]:border-b
          [&_th]:border-slate-dark
          [&_th]:bg-charcoal

          [&_td]:px-4
          [&_td]:py-3
          [&_td]:border-b
          [&_td]:border-slate-dark
          [&_td]:text-stone-400

          [&_tr:hover_td]:bg-gold/5
          [&_tr:last-child_td]:border-b-0

          [&_.num]:text-right
          [&_.num]:font-mono
          [&_.num]:font-medium
          [&_.num]:text-ivory
          [&_.num]:tabular-nums

          [&_.total-row]:font-bold
          [&_.total-row]:bg-charcoal
          [&_.total-row_td]:border-t-2
          [&_.total-row_td]:border-t-gold
          [&_.total-row_td]:text-gold
        `}
      >
        {children}
      </table>
    </div>
  );
}
